import * as React from "react";
import { nullToUndefined, sleep } from "common/Support";
import PubSub from "pubsub-js";
import { SCROLL_CHANGED } from "../../../lib/constants";
import { EvaluatedScreen } from "common/components/types";
import { scrollIDForScreenID } from "../scroll-reactive-component/scroll-reactive-component";

interface ScrollHistoryProps {
    stateKey: EvaluatedScreen;
    target: string;
}

export default class ScrollHistory extends React.Component<ScrollHistoryProps> {
    private static scrollMap: Map<string, number> = new Map<string, number>();

    private get scrollElement(): Element | undefined {
        const { target } = this.props;
        return nullToUndefined(document.getElementById(target));
    }

    public componentDidMount(): void {
        this.updateScroll();
    }

    public componentWillUnmount(): void {
        this.saveScroll();
    }

    public shouldComponentUpdate(nextProps: ScrollHistoryProps): boolean {
        const { stateKey } = this.props;
        const nextId = nextProps.stateKey;

        if (stateKey === nextId) return false;

        this.saveScroll();

        return true;
    }

    private saveScroll(): void {
        const scrollElement = this.scrollElement;

        if (scrollElement === null) return;

        if (scrollElement !== undefined) {
            ScrollHistory.scrollMap.set(this.props.stateKey.screenId, scrollElement.scrollTop);
        }
    }

    private updateScroll(): void {
        const { stateKey } = this.props;

        const scrollElement = this.scrollElement;

        if (scrollElement === null || scrollElement === undefined) return;

        let scrollValue = ScrollHistory.scrollMap.get(stateKey.screenId);
        if (scrollValue === undefined) {
            scrollValue = 0;
        }

        const sv = scrollValue;

        const updateScrollCallback = () => {
            // Basically scrolling across huge jumps at a single time
            // really upsets react-virtualized. So we limit ourselves to
            // 3000px at a time. This really makes react-virtualized much
            // happier. It's a dumb hack.
            if (sv > 3000) {
                let x = 0;
                const iter = (): void => {
                    x = Math.min(x + 3000, sv);
                    scrollElement.scrollTop = x;

                    if (x !== scrollValue) {
                        window.requestAnimationFrame(() => {
                            iter();
                        });
                    }
                };

                iter();
            } else {
                scrollElement.scrollTop = sv;
                PubSub.publishSync(SCROLL_CHANGED, {
                    position: scrollElement.scrollTop,
                    id: scrollIDForScreenID(stateKey.screenId),
                });
            }
        };

        const canScroll = () => scrollElement.scrollHeight - scrollElement.clientHeight >= sv;

        setTimeout(async () => {
            if (canScroll()) {
                updateScrollCallback();
            } else {
                for (let x = 0; x < 10; x++) {
                    await sleep(0 + x * 10);
                    if (canScroll()) {
                        updateScrollCallback();
                        break;
                    }
                }
            }
        }, 0);
    }

    public render() {
        return null;
    }
}
